import { analyzeImageWithGemini } from './LegalAPIs';

const getBackendBaseUrl = () => {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return 'https://rda-backend-thuq.onrender.com';
};

const cleanExtractedText = (value) => {
  return String(value || '')
    .replace(/[|]+/g, ' | ')
    .replace(/[;]+/g, ' ; ')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizePersonLikeName = (value) => {
  const cleaned = cleanExtractedText(value)
    .replace(/^(?:nome da m[ãa]e|m[ãa]e|genitora|filia[cç][ãa]o materna)\s*[:\-]?\s*/i, '')
    .replace(/^(?:nome do pai|pai|genitor|filia[cç][ãa]o paterna)\s*[:\-]?\s*/i, '')
    .replace(/^(?:sr\.?|sra\.?|dr\.?|dra\.?)\s+/i, '')
    .replace(/\b(?:n[aã]o informado|nao informado|ignorado|desconhecido|desconhecida|ileg[ií]vel|ilegiv[eé]l|n\/a|na|--|-)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned;
};

const splitFiliationCandidates = (value) => {
  const cleaned = cleanExtractedText(value);
  if (!cleaned) return [];

  return cleaned
    .split(/\s*\|\s*|\s*;\s*|\s+e\s+|\s+&\s+|\s+\+\s+|\s*\/\s*/i)
    .map((part) => normalizePersonLikeName(part))
    .filter(Boolean);
};

const extractStructuredFiliation = (payload) => {
  const rawFiliation = payload?.filiation;
  const structuredMother = payload?.motherName || payload?.filiation?.motherName || payload?.mae || payload?.mother;
  const structuredFather = payload?.fatherName || payload?.filiation?.fatherName || payload?.pai || payload?.father;

  const parts = splitFiliationCandidates(rawFiliation);

  const motherName = normalizePersonLikeName(structuredMother || parts[0] || '');
  const fatherName = normalizePersonLikeName(structuredFather || parts.slice(1).join(' e ') || '');

  const filiation = cleanExtractedText(
    payload?.filiation || [motherName, fatherName].filter(Boolean).join(' e ')
  );

  return { motherName, fatherName, filiation };
};

export const normalizeDocumentExtractedData = (payload) => {
  const source = payload && typeof payload === 'object' ? payload : {};
  const { motherName, fatherName, filiation } = extractStructuredFiliation(source);

  return {
    ...source,
    name: cleanExtractedText(source?.name || ''),
    cpf: cleanExtractedText(source?.cpf || ''),
    birthDate: cleanExtractedText(source?.birthDate || ''),
    motherName,
    fatherName,
    filiation,
  };
};

const extractBase64Payload = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const commaIndex = raw.indexOf(',');
  if (raw.startsWith('data:') && commaIndex >= 0) {
    return String(raw.slice(commaIndex + 1) || '').trim();
  }
  return raw;
};

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(extractBase64Payload(reader.result));
      reader.onerror = () => reject(new Error('Falha ao converter arquivo para Base64.'));
      reader.readAsDataURL(blob);
    } catch (e) {
      reject(e);
    }
  });
};

export const urlToBase64 = async (imageUrl) => {
  const source = String(imageUrl || '').trim();
  if (!source) throw new Error('URL da imagem ausente para conversão Base64.');

  if (source.startsWith('data:')) {
    return extractBase64Payload(source);
  }

  const attempts = [
    async () => fetch(source, { method: 'GET', mode: 'cors', credentials: 'omit' }),
  ];

  const driveStreamMatch = source.match(/\/drive\/stream\/([^/?#]+)/i);
  const safeFileId = String(driveStreamMatch?.[1] || '').trim();
  if (safeFileId) {
    attempts.push(async () => fetch(`${getBackendBaseUrl()}/drive/stream/${encodeURIComponent(safeFileId)}`, {
      method: 'GET',
      credentials: 'omit',
    }));
  }

  let lastError = null;

  for (const attempt of attempts) {
    try {
      const response = await attempt();
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`Falha ao baixar o arquivo para análise (${response.status}). ${errText}`.trim());
      }
      const blob = await response.blob();
      return await blobToBase64(blob);
    } catch (error) {
      lastError = error;
    }
  }

  throw new Error(`Falha ao converter URL em Base64 para análise multimodal: ${String(lastError?.message || lastError || 'erro desconhecido')}`);
};

const fetchFileAsBase64 = async (urlOrFile) => {
  if (typeof File !== 'undefined' && urlOrFile instanceof File) {
    return blobToBase64(urlOrFile);
  }

  if (typeof Blob !== 'undefined' && urlOrFile instanceof Blob) {
    return blobToBase64(urlOrFile);
  }

  const source = String(urlOrFile || '').trim();
  if (!source) throw new Error('Arquivo sem origem legível para análise.');

  if (source.startsWith('data:')) {
    return extractBase64Payload(source);
  }

  return urlToBase64(source);
};

const cleanJsonResponse = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
};

const resolveInputMimeType = (source, mimeType = '') => {
  const explicit = String(mimeType || '').trim().toLowerCase();
  if (explicit) return explicit;
  const safeSource = String(source || '').trim().toLowerCase();
  if (safeSource.startsWith('data:image/png')) return 'image/png';
  if (safeSource.startsWith('data:image/webp')) return 'image/webp';
  if (safeSource.startsWith('data:image/jpeg') || safeSource.startsWith('data:image/jpg')) return 'image/jpeg';
  if (safeSource.startsWith('data:application/pdf')) return 'application/pdf';
  if (/\.png([?#].*)?$/.test(safeSource)) return 'image/png';
  if (/\.webp([?#].*)?$/.test(safeSource)) return 'image/webp';
  if (/\.pdf([?#].*)?$/.test(safeSource)) return 'application/pdf';
  return 'image/jpeg';
};

const parseAiExtractionPayload = (payload) => {
  const source = payload && typeof payload === 'object' ? payload : {};
  const rawExtractedData = source?.extractedData && typeof source.extractedData === 'object'
    ? source.extractedData
    : source?.data && typeof source.data === 'object'
      ? source.data
      : null;
  const extractedData = rawExtractedData ? normalizeDocumentExtractedData(rawExtractedData) : null;
  return {
    success: Boolean(source?.success !== false),
    quality: String(source?.quality || 'GOOD').trim().toUpperCase() || 'GOOD',
    documentType: String(source?.documentType || source?.document || 'DESCONHECIDO').trim() || 'DESCONHECIDO',
    extractedData,
    reason: String(source?.reason || '').trim(),
  };
};

export const extractDocumentData = async (fileUrlOrBlob, mimeType = '') => {
  const hasGeminiApiKey = Boolean(String(import.meta?.env?.VITE_GEMINI_API_KEY || '').trim());
  const hasGeminiProxy = Boolean(String(import.meta?.env?.VITE_GEMINI_PROXY_URL || '').trim());
  const safeMimeType = resolveInputMimeType(fileUrlOrBlob, mimeType);

  if (!fileUrlOrBlob) {
    return {
      success: false,
      quality: 'BLURRED',
      documentType: 'DESCONHECIDO',
      extractedData: null,
      reason: 'Arquivo sem origem legível para análise.',
    };
  }

  try {
    const base64Data = await fetchFileAsBase64(fileUrlOrBlob);
    if (!base64Data) {
      throw new Error('Falha ao converter o documento para Base64 antes da análise multimodal.');
    }

    const systemPrompt = 'Atue como um Perito Forense Documental. Analise a imagem. Se estiver ilegível, retorne APENAS o JSON: {"quality": "BLURRED", "success": false}. Se legível, extraia e retorne APENAS o JSON ESTRITO: {"quality": "GOOD", "success": true, "documentType": "RG/CNH/COMPROVANTE", "extractedData": {"name": "...", "cpf": "...", "birthDate": "AAAA-MM-DD", "filiation": ["Nome da Mãe", "Nome do Pai"]}}.';

    const rawResponse = await analyzeImageWithGemini(base64Data, safeMimeType, systemPrompt);
    const cleaned = cleanJsonResponse(rawResponse);

    if (!cleaned) {
      throw new Error('Gemini retornou payload vazio para a perícia documental.');
    }

    return parseAiExtractionPayload(JSON.parse(cleaned));
  } catch (e) {
    throw new Error(`Falha na extração documental multimodal: ${String(e?.message || e)}`);
  }
 };

/*
Integração futura com Google Gemini Vision (payload inlineData pronto para produção):

const body = {
  contents: [
    {
      parts: [
        { text: systemPrompt },
        { inlineData: { mimeType: safeMimeType, data: base64Data } }
      ]
    }
  ],
  generationConfig: {
    temperature: 0.1,
    responseMimeType: 'application/json'
  }
};

O retorno esperado deve obedecer a este contrato:
{
  "success": true,
  "quality": "GOOD",
  "documentType": "CNH",
  "extractedData": {
    "name": "NOME COMPLETO",
    "cpf": "000.000.000-00",
    "birthDate": "1980-01-31",
    "filiation": ["Nome 1", "Nome 2"]
  }
}
*/
